import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminBulkActionRow,
  AdminSectionCard,
  ConfirmDialog,
  HelpAnchor,
  InlineError,
  InputField,
  Modal,
  Pill,
  SkeletonRows,
  Table,
  TBody,
  TD,
  TextareaField,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import {
  BUTTON_GHOST_ADMIN,
  BUTTON_PRIMARY,
} from '@/shared/ui-class';
import {
  SEED_SYSTEM_PROMPT_VERSIONS,
  SYSTEM_PROMPT_KITS,
  type SystemPromptKit,
  type SystemPromptVersionMeta,
} from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import { AdminRowMenu } from './AdminRowMenu';
import {
  availableForNewCount,
  bodyBytes,
  buildScopesFromVersions,
  composeMapRows,
  enabledModelsForKit,
  findAdkTemplateKeys,
  findScope,
  formatPromptDate,
  kitModelLabelMap,
  KIT_LABELS,
  overrideCount,
  promptLabel,
  publishDraftVersion,
  refreshScopesForKit,
  rollbackToVersion,
  scopeDisplayName,
  sdkShortLabel,
  SHIPPED_SYSTEM_PROMPT_BASE,
  statusTone,
  SYSTEM_PROMPT_BODY_MAX_BYTES,
  upsertDraftVersion,
  discardDraftVersion,
  type MapRow,
  type ModalState,
  type ScopeEntry,
} from './SystemPromptScene.transform';
import { getModel } from './AiModelsScene.transform';

// ─── BodyInput ──────────────────────────────────────────────────────────────

function BodyInput({
  kit,
  value,
  onChange,
  disabled,
  label = 'Body',
}: {
  kit: SystemPromptKit;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  const byteCount = useMemo(() => bodyBytes(value), [value]);
  const overCap = byteCount > SYSTEM_PROMPT_BODY_MAX_BYTES;
  const nearCap = byteCount >= SYSTEM_PROMPT_BODY_MAX_BYTES * 0.9;
  const googleLintKeys = kit === 'google' ? findAdkTemplateKeys(value) : [];

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <TextareaField
        className="min-h-48 font-mono text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      <div
        className={cn(
          'text-xs',
          overCap ? 'text-red-600' : nearCap ? 'text-amber-600' : 'text-muted',
        )}
      >
        {byteCount.toLocaleString()} / {SYSTEM_PROMPT_BODY_MAX_BYTES.toLocaleString()} bytes
      </div>
      {googleLintKeys.length > 0 ? (
        <p className="text-xs text-red-600">
          Google bodies must not contain ADK template keys: {googleLintKeys.join(', ')}
        </p>
      ) : null}
    </div>
  );
}

// ─── VersionHistorySection ──────────────────────────────────────────────────

function VersionHistorySection({
  versions,
  liveVersion,
  onViewVersion,
  onRollback,
}: {
  versions: SystemPromptVersionMeta[];
  liveVersion: number | null;
  onViewVersion: (versionId: string) => void;
  onRollback: (version: SystemPromptVersionMeta) => void;
}) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>Version</TH>
          <TH>Status</TH>
          <TH>Author</TH>
          <TH>Created</TH>
          <TH>Published</TH>
          <TH>Actions</TH>
        </TR>
      </THead>
      <TBody>
        {versions.map((version) => {
          const isLive = version.version === liveVersion;
          const canRollback =
            version.publishedAt !== null && !isLive && version.status !== 'draft';
          return (
            <TR key={version.id}>
              <TD>v{version.version}</TD>
              <TD>
                <Pill tone={statusTone(version.status)}>
                  {isLive ? 'live' : version.status}
                </Pill>
              </TD>
              <TD>{version.createdByEmail ?? '—'}</TD>
              <TD>{formatPromptDate(version.createdAt)}</TD>
              <TD>{version.publishedAt ? formatPromptDate(version.publishedAt) : '—'}</TD>
              <TD>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs text-muted underline hover:text-zinc-700 dark:hover:text-zinc-300"
                    onClick={() => onViewVersion(version.id)}
                  >
                    View
                  </button>
                  {canRollback ? (
                    <button
                      type="button"
                      className="text-xs text-muted underline hover:text-zinc-700 dark:hover:text-zinc-300"
                      onClick={() => onRollback(version)}
                    >
                      Rollback…
                    </button>
                  ) : null}
                </div>
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

// ─── MapRowMenu ─────────────────────────────────────────────────────────────

function MapRowMenu({
  row,
  hasDraft,
  onEdit,
  onDuplicate,
  onDiscard,
}: {
  row: MapRow;
  hasDraft: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDiscard: () => void;
}) {
  return (
    <AdminRowMenu
      label="Row actions"
      items={[
        { label: 'Edit', onSelect: onEdit },
        ...(row.scope
          ? [{ label: 'Duplicate', onSelect: onDuplicate }]
          : []),
        {
          label: 'Discard draft',
          onSelect: onDiscard,
          disabled: !hasDraft,
        },
      ]}
    />
  );
}

function versionStatusCell(scope: ScopeEntry | null): ReactNode {
  if (!scope) return <span className="text-muted">—</span>;
  const { state } = scope;
  if (state.draft) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {state.liveVersion != null ? (
          <span className="text-sm text-muted">v{state.liveVersion} · live</span>
        ) : state.usesShippedDefault ? (
          <Pill tone="default">shipped default</Pill>
        ) : null}
        <Pill tone="danger">draft</Pill>
      </div>
    );
  }
  if (state.usesShippedDefault) {
    return <Pill tone="default">shipped default</Pill>;
  }
  if (state.liveVersion != null) {
    return (
      <span className="text-sm text-muted">v{state.liveVersion} · live</span>
    );
  }
  return <span className="text-muted">—</span>;
}

// ─── ScopeModal ─────────────────────────────────────────────────────────────

function ScopeModal({
  open,
  mode,
  allScopesByKit,
  saving,
  formError,
  draftText,
  onDraftTextChange,
  onClose,
  onSaveDraft,
  onPublish,
  onDiscard,
  onSaveAndPublish,
  onSaveCreateDraft,
  onViewVersion,
  onRollback,
}: {
  open: boolean;
  mode: ModalState;
  allScopesByKit: Partial<Record<SystemPromptKit, ScopeEntry[]>>;
  saving: boolean;
  formError: string | null;
  draftText: string;
  onDraftTextChange: (value: string) => void;
  onClose: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  onDiscard: () => void;
  onSaveAndPublish: (params: {
    name: string;
    body: string;
    modelId: string;
    targetKit: SystemPromptKit;
  }) => void;
  onSaveCreateDraft: (params: {
    name: string;
    body: string;
    modelId: string;
    targetKit: SystemPromptKit;
  }) => void;
  onViewVersion: (versionId: string) => void;
  onRollback: (version: SystemPromptVersionMeta) => void;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [compareLive, setCompareLive] = useState(false);

  const isEdit = mode.kind === 'edit';
  const isCreate = mode.kind === 'create';
  const isDuplicate = mode.kind === 'duplicate';

  const activeKit =
    mode.kind === 'edit'
      ? mode.kit
      : mode.kind === 'create'
        ? mode.forKit
        : mode.sourceKit;

  const editScope = isEdit ? mode.scope : null;

  const defaultName = useMemo(() => {
    if (isDuplicate) {
      const rawName =
        mode.sourceScope.name ??
        (mode.sourceScope.modelId === null
          ? `Default — ${KIT_LABELS[mode.sourceKit]}`
          : null) ??
        mode.sourceScope.modelId ??
        'Prompt';
      return `${rawName} (copy)`;
    }
    return '';
  }, [isDuplicate, mode]);

  const [name, setName] = useState(defaultName);
  const [createBody, setCreateBody] = useState(isDuplicate ? mode.sourceBody : '');
  const [selectedModelId, setSelectedModelId] = useState(
    isCreate ? (mode.presetModelId ?? '') : '',
  );
  const [targetKit, setTargetKit] = useState<SystemPromptKit>(
    isCreate ? mode.forKit : isDuplicate ? mode.sourceKit : activeKit,
  );

  useEffect(() => {
    if (!open) return;
    setHistoryOpen(false);
    setCompareLive(false);
    if (isCreate) {
      setName('');
      setCreateBody('');
      setSelectedModelId(mode.presetModelId ?? '');
      setTargetKit(mode.forKit);
    } else if (isDuplicate) {
      setName(defaultName);
      setCreateBody(mode.sourceBody);
      setSelectedModelId('');
      setTargetKit(mode.sourceKit);
    }
  }, [open, mode, isCreate, isDuplicate, defaultName]);

  const modelOptions = useMemo(() => {
    if (isCreate) {
      const existingScopeModelIds = new Set(
        (allScopesByKit[mode.forKit] ?? [])
          .map((scope) => scope.modelId)
          .filter((id): id is string => id !== null),
      );
      return enabledModelsForKit(mode.forKit).filter(
        (model) => !existingScopeModelIds.has(model.id),
      );
    }
    if (isDuplicate) return enabledModelsForKit(targetKit);
    return [];
  }, [isCreate, isDuplicate, mode, targetKit, allScopesByKit]);

  const claudeOptions = modelOptions.filter((model) => model.kit === 'claude');
  const googleOptions = modelOptions.filter((model) => model.kit === 'google');

  const bodyValue = isEdit ? draftText : createBody;
  const bodyKit = isEdit ? activeKit : targetKit;
  const setBodyValue = isEdit ? onDraftTextChange : setCreateBody;

  const byteCount = useMemo(() => bodyBytes(bodyValue), [bodyValue]);
  const overCap = byteCount > SYSTEM_PROMPT_BODY_MAX_BYTES;
  const googleLintKeys = bodyKit === 'google' ? findAdkTemplateKeys(bodyValue) : [];

  const trimmedName = name.trim();
  const nameError =
    !isEdit && trimmedName.length === 0
      ? 'Name is required.'
      : !isEdit && trimmedName.length > 120
        ? 'Name must be 120 characters or fewer.'
        : null;

  const canSaveEdit =
    isEdit &&
    !overCap &&
    googleLintKeys.length === 0 &&
    draftText.trim().length > 0 &&
    !saving;

  const canSubmitCreate =
    (isCreate || isDuplicate) &&
    !saving &&
    !nameError &&
    selectedModelId !== '' &&
    createBody.trim().length > 0 &&
    !overCap &&
    googleLintKeys.length === 0;

  const liveBody = editScope?.state.live?.body ?? SHIPPED_SYSTEM_PROMPT_BASE;

  const modalTitle = isEdit
    ? `Edit — ${scopeDisplayName(activeKit, editScope!.modelId, editScope!.name)}`
    : isDuplicate
      ? 'Duplicate prompt'
      : 'New system prompt';

  const lockedModelLabel = isEdit
    ? editScope!.modelId === null
      ? `Default — all ${KIT_LABELS[activeKit]} models`
      : (getModel(editScope!.modelId)?.label ?? editScope!.modelId)
    : null;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose();
      }}
      title={modalTitle}
      className="w-[min(720px,calc(100vw-2rem))]"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          This is the <em>base</em> prompt only. Memory, project context, skills,
          platform tools, artifact and form-mode blocks are appended by the
          application and are not editable here. On Gemini, the runtime identity
          header and system invariants additionally wrap this text.
        </p>

        {!isEdit ? (
          <p className="text-sm text-muted">
            To assign a prompt to a different model, duplicate it to the new
            model. Scope identity is <em>(SDK, model)</em> — history stays with
            its original scope.
          </p>
        ) : null}

        {formError ? <InlineError>{formError}</InlineError> : null}

        {!isEdit ? (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Name <span className="text-red-500">*</span>
            </label>
            <InputField
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Customer support agent"
              maxLength={130}
              disabled={saving}
            />
            {nameError && name.length > 0 ? (
              <p className="text-xs text-red-600">{nameError}</p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-1">
            <span className="text-sm font-medium">Name</span>
            <p className="text-sm text-muted">
              {scopeDisplayName(activeKit, editScope!.modelId, editScope!.name)}
            </p>
          </div>
        )}

        {isEdit ? (
          <div className="space-y-1">
            <span className="text-sm font-medium">Model</span>
            <p className="text-sm text-muted">{lockedModelLabel}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Model <span className="text-red-500">*</span>
            </label>
            {modelOptions.length === 0 ? (
              <p className="text-sm text-muted">
                All enabled{' '}
                {KIT_LABELS[isCreate ? mode.forKit : mode.sourceKit]} models
                already have a prompt.
              </p>
            ) : (
              <select
                className="w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus disabled:opacity-60"
                value={selectedModelId}
                onChange={(event) => {
                  setSelectedModelId(event.target.value);
                  const model = getModel(event.target.value);
                  if (model) setTargetKit(model.kit);
                }}
                disabled={saving}
              >
                <option value="" disabled>
                  Select a model…
                </option>
                {isDuplicate ? (
                  <>
                    {claudeOptions.length > 0 ? (
                      <optgroup label="Anthropic">
                        {claudeOptions.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.label}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                    {googleOptions.length > 0 ? (
                      <optgroup label="Google models">
                        {googleOptions.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.label}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </>
                ) : (
                  modelOptions.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.label}
                    </option>
                  ))
                )}
              </select>
            )}
          </div>
        )}

        <BodyInput
          kit={bodyKit}
          value={bodyValue}
          onChange={setBodyValue}
          disabled={saving}
          label={isEdit ? 'Draft editor' : 'Body'}
        />

        {isEdit ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={compareLive}
              onChange={(event) => setCompareLive(event.target.checked)}
              className="rounded border-divider"
            />
            Compare with live
          </label>
        ) : null}

        {isEdit && compareLive ? (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-sm font-medium">Draft</h3>
              <pre className="max-h-48 overflow-auto rounded-lg border border-divider p-3 text-sm whitespace-pre-wrap">
                {draftText || '(empty)'}
              </pre>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-medium">Live</h3>
              <pre className="max-h-48 overflow-auto rounded-lg border border-divider p-3 text-sm whitespace-pre-wrap">
                {liveBody}
              </pre>
            </div>
          </div>
        ) : null}

        {isEdit && editScope ? (
          <div className="border-t border-divider pt-4">
            <button
              type="button"
              className="flex w-full items-center justify-between text-sm font-medium"
              onClick={() => setHistoryOpen((value) => !value)}
            >
              Version history
              {historyOpen ? (
                <ChevronUp className="h-4 w-4 text-muted" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted" />
              )}
            </button>
            {historyOpen ? (
              <div className="mt-3">
                <VersionHistorySection
                  versions={editScope.state.versions}
                  liveVersion={editScope.state.liveVersion}
                  onViewVersion={onViewVersion}
                  onRollback={onRollback}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 border-t border-divider pt-4">
          {isEdit ? (
            <>
              <button
                type="button"
                className={BUTTON_GHOST_ADMIN}
                disabled={!canSaveEdit}
                onClick={onSaveDraft}
              >
                Save draft
              </button>
              <button
                type="button"
                className={BUTTON_GHOST_ADMIN}
                disabled={!editScope?.state.draft || saving}
                onClick={onPublish}
              >
                Publish…
              </button>
              <button
                type="button"
                className={BUTTON_GHOST_ADMIN}
                disabled={!editScope?.state.draft || saving}
                onClick={onDiscard}
              >
                Discard draft…
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={BUTTON_GHOST_ADMIN}
                disabled={!canSubmitCreate}
                onClick={() =>
                  onSaveCreateDraft({
                    name: trimmedName,
                    body: createBody,
                    modelId: selectedModelId,
                    targetKit,
                  })
                }
              >
                Save draft
              </button>
              <button
                type="button"
                className={BUTTON_PRIMARY}
                disabled={!canSubmitCreate}
                onClick={() =>
                  onSaveAndPublish({
                    name: trimmedName,
                    body: createBody,
                    modelId: selectedModelId,
                    targetKit,
                  })
                }
              >
                Save &amp; publish…
              </button>
            </>
          )}
          <button
            type="button"
            className={BUTTON_GHOST_ADMIN}
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── SystemPromptScene ──────────────────────────────────────────────────────

export function SystemPromptScene() {
  const [kit, setKit] = useState<SystemPromptKit>('claude');
  const [versions, setVersions] = useState(SEED_SYSTEM_PROMPT_VERSIONS);

  const enabledModels = useMemo(() => enabledModelsForKit(kit), [kit]);
  const enabledModelIds = useMemo(
    () => new Set(enabledModels.map((model) => model.id)),
    [enabledModels],
  );
  const kitModelLabels = useMemo(() => kitModelLabelMap(kit), [kit]);

  const scopesByKit = useMemo(() => {
    const map: Partial<Record<SystemPromptKit, ScopeEntry[]>> = {};
    for (const k of SYSTEM_PROMPT_KITS) {
      map[k] = buildScopesFromVersions(versions, k);
    }
    return map;
  }, [versions]);

  const scopes = useMemo(() => scopesByKit[kit] ?? [], [scopesByKit, kit]);

  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [viewBody, setViewBody] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [discardTarget, setDiscardTarget] = useState<{
    kit: SystemPromptKit;
    modelId: string | null;
  } | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<{
    kit: SystemPromptKit;
    modelId: string | null;
    version: SystemPromptVersionMeta;
  } | null>(null);
  const [pendingPublish, setPendingPublish] = useState<{
    kit: SystemPromptKit;
    modelId: string | null;
    body: string;
    name: string | null;
  } | null>(null);

  const mapRows = useMemo(
    () => composeMapRows(kit, scopes, enabledModelIds, kitModelLabels),
    [kit, scopes, enabledModelIds, kitModelLabels],
  );

  const editScope = useMemo(() => {
    if (modalState?.kind !== 'edit') return null;
    return modalState.scope;
  }, [modalState]);

  const overrides = useMemo(() => overrideCount(scopes), [scopes]);
  const newPromptCount = useMemo(
    () => availableForNewCount(scopes, kitModelLabels),
    [scopes, kitModelLabels],
  );

  useEffect(() => {
    if (editScope) {
      setDraftText(
        editScope.state.draft?.body ?? editScope.state.live?.body ?? '',
      );
    }
  }, [editScope]);

  const closeModal = useCallback(() => {
    if (saving) return;
    setModalOpen(false);
    setModalState(null);
    setFormError(null);
    setPendingPublish(null);
  }, [saving]);

  const openEditModal = useCallback(
    (scope: ScopeEntry) => {
      setModalState({ kind: 'edit', kit, scope });
      setFormError(null);
      setModalOpen(true);
    },
    [kit],
  );

  const openCreateModal = useCallback(
    (presetModelId?: string) => {
      setModalState({ kind: 'create', forKit: kit, presetModelId });
      setFormError(null);
      setModalOpen(true);
    },
    [kit],
  );

  const handleRowClick = useCallback(
    (row: MapRow) => {
      if (row.modelId === null) {
        const scope = findScope(scopes, null);
        if (scope) openEditModal(scope);
        return;
      }
      if (row.scope) {
        openEditModal(row.scope);
      } else {
        openCreateModal(row.modelId);
      }
    },
    [scopes, openEditModal, openCreateModal],
  );

  const handleDuplicate = useCallback(
    (scope: ScopeEntry) => {
      const sourceBody =
        scope.state.live?.body ?? scope.state.draft?.body ?? '';
      setModalState({
        kind: 'duplicate',
        sourceScope: scope,
        sourceKit: kit,
        sourceBody,
      });
      setFormError(null);
      setModalOpen(true);
    },
    [kit],
  );

  const saveDraft = useCallback(() => {
    if (modalState?.kind !== 'edit') return;
    const { kit: targetKit, scope } = modalState;
    setSaving(true);
    setFormError(null);
    setVersions((prev) =>
      upsertDraftVersion(
        prev,
        targetKit,
        scope.modelId,
        draftText,
        scope.name,
      ),
    );
    const refreshed = refreshScopesForKit(
      upsertDraftVersion(
        versions,
        targetKit,
        scope.modelId,
        draftText,
        scope.name,
      ),
      targetKit,
    );
    const fresh = findScope(refreshed, scope.modelId);
    if (fresh) {
      setModalState({ kind: 'edit', kit: targetKit, scope: fresh });
    }
    setSaving(false);
  }, [draftText, modalState, versions]);

  const publish = useCallback(() => {
    if (!pendingPublish) return;
    const { kit: targetKit, modelId, body, name } = pendingPublish;
    setSaving(true);
    setFormError(null);
    setVersions((prev) => publishDraftVersion(prev, targetKit, modelId, body, name));
    setConfirmPublish(false);
    setPendingPublish(null);
    closeModal();
    if (targetKit !== kit) setKit(targetKit);
    setSaving(false);
  }, [closeModal, kit, pendingPublish]);

  const discard = useCallback(() => {
    if (!discardTarget) return;
    const { kit: targetKit, modelId } = discardTarget;
    const fromEditModal =
      modalOpen &&
      modalState?.kind === 'edit' &&
      modalState.kit === targetKit &&
      modalState.scope.modelId === modelId;
    setSaving(true);
    if (fromEditModal) setFormError(null);
    else setError(null);
    setVersions((prev) => discardDraftVersion(prev, targetKit, modelId));
    setDiscardTarget(null);
    if (fromEditModal) closeModal();
    setSaving(false);
  }, [closeModal, discardTarget, modalOpen, modalState]);

  const rollback = useCallback(() => {
    if (!rollbackTarget) return;
    const { kit: targetKit, modelId, version } = rollbackTarget;
    setSaving(true);
    setFormError(null);
    setVersions((prev) => rollbackToVersion(prev, targetKit, modelId, version));
    setDraftText(version.body);
    setRollbackTarget(null);
    const refreshed = refreshScopesForKit(
      rollbackToVersion(versions, targetKit, modelId, version),
      targetKit,
    );
    if (modalState?.kind === 'edit' && modalState.kit === targetKit) {
      const fresh = findScope(refreshed, modalState.scope.modelId);
      if (fresh) {
        setModalState({ kind: 'edit', kit: targetKit, scope: fresh });
      }
    }
    setSaving(false);
  }, [modalState, rollbackTarget, versions]);

  const viewVersion = useCallback(
    (versionId: string) => {
      if (modalState?.kind !== 'edit') return;
      const version = modalState.scope.state.versions.find((v) => v.id === versionId);
      if (version) setViewBody(version.body);
    },
    [modalState],
  );

  const handleFormSaveDraft = useCallback(
    (params: {
      name: string;
      body: string;
      modelId: string;
      targetKit: SystemPromptKit;
    }) => {
      setSaving(true);
      setFormError(null);
      setVersions((prev) =>
        upsertDraftVersion(
          prev,
          params.targetKit,
          params.modelId,
          params.body,
          params.name,
        ),
      );
      closeModal();
      if (params.targetKit !== kit) setKit(params.targetKit);
      const refreshed = refreshScopesForKit(
        upsertDraftVersion(
          versions,
          params.targetKit,
          params.modelId,
          params.body,
          params.name,
        ),
        params.targetKit,
      );
      const fresh = findScope(refreshed, params.modelId);
      if (fresh) {
        setModalState({ kind: 'edit', kit: params.targetKit, scope: fresh });
        setModalOpen(true);
      }
      setSaving(false);
    },
    [closeModal, kit, versions],
  );

  const handleFormSaveAndPublish = useCallback(
    (params: {
      name: string;
      body: string;
      modelId: string;
      targetKit: SystemPromptKit;
    }) => {
      setPendingPublish({
        kit: params.targetKit,
        modelId: params.modelId,
        body: params.body,
        name: params.name,
      });
      setConfirmPublish(true);
    },
    [],
  );

  const publishScopeLabel = useMemo(() => {
    if (!pendingPublish) return KIT_LABELS[kit];
    if (pendingPublish.modelId === null) {
      return `Default — ${KIT_LABELS[pendingPublish.kit]}`;
    }
    return (
      getModel(pendingPublish.modelId)?.label ?? pendingPublish.modelId
    );
  }, [kit, pendingPublish]);

  const discardScopeLabel = useMemo(() => {
    if (!discardTarget) return 'this scope';
    const scope = (scopesByKit[discardTarget.kit] ?? []).find(
      (entry) => entry.modelId === discardTarget.modelId,
    );
    return scope
      ? scopeDisplayName(discardTarget.kit, scope.modelId, scope.name)
      : 'this scope';
  }, [discardTarget, scopesByKit]);

  return (
    <AdminSceneShell
      sceneName="SystemPromptScene"
      title="System prompts"
      description="Which base prompt each model uses. Override per model, or set an SDK default."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-6"
    >
      <Commentable name="KitTabs">
        <div className="flex gap-1 border-b border-divider">
          {SYSTEM_PROMPT_KITS.map((k) => (
            <button
              key={k}
              type="button"
              className={cn(
                'px-3 py-2 text-sm font-medium transition-colors',
                kit === k
                  ? 'border-b-2 border-zinc-900 text-zinc-900 dark:border-zinc-100 dark:text-zinc-100'
                  : 'text-muted hover:text-zinc-700 dark:hover:text-zinc-200',
              )}
              onClick={() => setKit(k)}
            >
              {KIT_LABELS[k]}
            </button>
          ))}
        </div>
      </Commentable>

      {error ? <InlineError>{error}</InlineError> : null}

      <Commentable name="Toolbar">
        <AdminBulkActionRow
          left={
            loading
              ? null
              : (
                <>
                  {kitModelLabels.size} model
                  {kitModelLabels.size === 1 ? '' : 's'} · {overrides} override
                  {overrides === 1 ? '' : 's'}
                </>
              )
          }
        >
          <button
            type="button"
            className={BUTTON_PRIMARY}
            onClick={() => openCreateModal()}
            disabled={loading || saving || modalOpen || newPromptCount === 0}
            title={
              kitModelLabels.size === 0
                ? 'No models are enabled for this SDK — add an API key in Provider keys, or enable models in AI models.'
                : newPromptCount === 0
                  ? 'Every enabled model already has a prompt — click a model below to edit it.'
                  : undefined
            }
          >
            <Plus className="h-4 w-4" />
            New system prompt
          </button>
        </AdminBulkActionRow>
      </Commentable>

      {!loading && kitModelLabels.size === 0 ? (
        <AdminSectionCard tone="info">
          <p className="text-sm text-muted">
            No {KIT_LABELS[kit]} models are enabled, so there are no per-model
            prompts to manage (the kit still uses its shipped default). This
            usually means the provider API key is missing — add it in{' '}
            <Link
              to="/admin/api-keys"
              className="underline hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              Provider keys
            </Link>
            , then enable models in{' '}
            <Link
              to="/admin/ai-models"
              className="underline hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              AI models
            </Link>
            .
          </p>
        </AdminSectionCard>
      ) : null}

      <Commentable name="MapTable">
        <AdminSectionCard title="Prompts by model">
          {loading ? (
            <SkeletonRows count={6} cols={4} />
          ) : (
            <Table wrap={false}>
              <THead>
                <TR>
                  <TH>Model</TH>
                  <TH>Prompt</TH>
                  <TH>Version · Status</TH>
                  <TH className="w-10" aria-label="Actions" />
                </TR>
              </THead>
              <TBody>
                {mapRows.map((row) => {
                  const sdkLabel = sdkShortLabel(kit);
                  return (
                    <TR
                      key={row.key}
                      className={cn(
                        row.isDisabled && 'opacity-60',
                        !row.isDisabled && 'cursor-pointer',
                      )}
                      onRowClick={
                        row.isDisabled ? undefined : () => handleRowClick(row)
                      }
                    >
                      <TD>
                        <div className="space-y-1">
                          <span
                            className={cn(
                              'text-sm',
                              row.modelId === null && 'font-medium',
                            )}
                          >
                            {row.modelLabel}
                          </span>
                          {row.isDisabled ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-muted">
                                Not in use → falls back to default.{' '}
                                <Link
                                  to="/admin/ai-models"
                                  className="underline hover:text-zinc-700 dark:hover:text-zinc-300"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  AI models
                                </Link>
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </TD>
                      <TD>
                        <span
                          className={cn(
                            'text-sm',
                            row.usesDefault && 'text-muted',
                          )}
                        >
                          {promptLabel(kit, row)}
                        </span>
                      </TD>
                      <TD>
                        {row.usesDefault ? (
                          <span className="text-sm text-muted">
                            inherits {sdkLabel} default
                          </span>
                        ) : (
                          versionStatusCell(row.scope)
                        )}
                      </TD>
                      <TD className="text-right">
                        {row.scope ? (
                          <span onClick={(event) => event.stopPropagation()}>
                            <MapRowMenu
                              row={row}
                              hasDraft={!!row.scope.state.draft}
                              onEdit={() => {
                                if (row.scope) openEditModal(row.scope);
                              }}
                              onDuplicate={() => {
                                if (row.scope) handleDuplicate(row.scope);
                              }}
                              onDiscard={() =>
                                setDiscardTarget({ kit, modelId: row.modelId })
                              }
                            />
                          </span>
                        ) : null}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </AdminSectionCard>
      </Commentable>

      {modalState ? (
        <ScopeModal
          open={modalOpen}
          mode={modalState}
          allScopesByKit={scopesByKit}
          saving={saving}
          formError={formError}
          draftText={draftText}
          onDraftTextChange={setDraftText}
          onClose={closeModal}
          onSaveDraft={saveDraft}
          onPublish={() => {
            if (modalState.kind !== 'edit') return;
            const draft = modalState.scope.state.draft;
            if (!draft) return;
            setPendingPublish({
              kit: modalState.kit,
              modelId: modalState.scope.modelId,
              body: draftText,
              name: modalState.scope.name,
            });
            setConfirmPublish(true);
          }}
          onDiscard={() => {
            if (modalState.kind !== 'edit') return;
            setDiscardTarget({
              kit: modalState.kit,
              modelId: modalState.scope.modelId,
            });
          }}
          onSaveAndPublish={handleFormSaveAndPublish}
          onSaveCreateDraft={handleFormSaveDraft}
          onViewVersion={viewVersion}
          onRollback={(version) => {
            if (modalState.kind !== 'edit') return;
            setRollbackTarget({
              kit: modalState.kit,
              modelId: modalState.scope.modelId,
              version,
            });
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirmPublish}
        onOpenChange={(open) => {
          setConfirmPublish(open);
          if (!open) setPendingPublish(null);
        }}
        title="Publish system prompt?"
        description={
          <>
            This applies to every user&apos;s next turn on the{' '}
            <strong>{publishScopeLabel}</strong> scope. Changes propagate within
            ~30 seconds across workers.
          </>
        }
        confirmLabel="Publish"
        onConfirm={publish}
      />

      <ConfirmDialog
        open={discardTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDiscardTarget(null);
        }}
        title="Discard draft?"
        description={
          <>
            The unpublished draft for{' '}
            <strong>{discardScopeLabel}</strong> will be permanently deleted.
          </>
        }
        confirmLabel="Discard"
        tone="danger"
        onConfirm={discard}
      />

      <ConfirmDialog
        open={rollbackTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRollbackTarget(null);
        }}
        title={
          rollbackTarget
            ? `Rollback to v${rollbackTarget.version.version}?`
            : 'Rollback?'
        }
        description="The live pointer will repoint to this prior published version."
        confirmLabel="Rollback"
        onConfirm={rollback}
      />

      {viewBody !== null ? (
        <Modal
          open
          onOpenChange={(open) => {
            if (!open) setViewBody(null);
          }}
          title="Version body"
          className="w-[min(720px,calc(100vw-2rem))]"
        >
          <pre className="max-h-96 overflow-auto rounded-lg border border-divider p-3 text-sm whitespace-pre-wrap">
            {viewBody}
          </pre>
        </Modal>
      ) : null}
    </AdminSceneShell>
  );
}
